import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Github, Eye, Code, Globe, Cpu, Database, Zap } from 'lucide-react';
import { featuredProjects, otherProjects, mlCollaborations, projectCategories } from '../data/projects';

// Import original projects from Projects.js
const originalWebProjects = {
  'psg': {
    title: 'A PSG win/lose Predictor website',
    details: 'Built using Python/Django and python data science.',
    technologies: ['Python', 'Django', 'Data Science', 'Machine Learning'],
    type: 'web',
    year: 2023,
    companyProject: false
  },
  'animeJojo': {
    title: 'A fullstack Anime Website',
    details: 'Built using Python/Django, Django rest framework and external apis.',
    technologies: ['Python', 'Django', 'Django REST Framework', 'APIs'],
    type: 'web',
    year: 2023,
    companyProject: false
  },
  'cinema': {
    title: 'A Movie search Website',
    details: 'Built using React and external apis.',
    technologies: ['React', 'JavaScript', 'APIs'],
    type: 'web',
    year: 2023,
    companyProject: false
  },
  'heart': {
    title: 'A Heart Failure Predictor website',
    details: 'Built using Python/Django and Python Data Science.',
    technologies: ['Python', 'Django', 'Data Science', 'Machine Learning'],
    type: 'web',
    year: 2023,
    companyProject: false
  },
  'oms': {
    title: 'An Online Marketing Site',
    details: 'Built using Python/Django.',
    technologies: ['Python', 'Django'],
    type: 'web',
    year: 2023,
    companyProject: false
  },
  'portfolio': {
    title: 'My Portfolio Website',
    details: 'Built using React.',
    technologies: ['React', 'JavaScript'],
    type: 'web',
    year: 2023,
    companyProject: false
  }
};

const originalAppProjects = {
  'repairs': {
    title: 'A Repairs App',
    details: 'Built using Flutter',
    technologies: ['Flutter', 'Dart', 'Mobile Development'],
    type: 'mobile',
    year: 2023,
    companyProject: false
  },
  'universally': {
    title: 'A Space App',
    details: 'Built using Kotlin and external apis with mvvm architecture.',
    technologies: ['Kotlin', 'MVVM', 'APIs', 'Mobile Development'],
    type: 'mobile',
    year: 2023,
    companyProject: false
  },
  'aberra': {
    title: 'A reminder app',
    details: 'Built using Kotlin with mvvm architecture.',
    technologies: ['Kotlin', 'MVVM', 'Mobile Development'],
    type: 'mobile',
    year: 2023,
    companyProject: false
  },
  'janim': {
    title: 'A note taking app and a trending movie/series app',
    details: 'Built using Kotlin, Room database and an external api.',
    technologies: ['Kotlin', 'Room Database', 'APIs', 'Mobile Development'],
    type: 'mobile',
    year: 2023,
    companyProject: false
  },
  'jotit': {
    title: 'A Todo App',
    details: 'An app for taking todos. Built using Kotlin and Room.',
    technologies: ['Kotlin', 'Room Database', 'Mobile Development'],
    type: 'mobile',
    year: 2023,
    companyProject: false
  }
};

const Portfolio = ({ value: { filter, handleFilter } }) => {

  const getCategoryIcon = (category) => {
    const icons = {
      'AI/Voice': Zap,
      'AI/IoT': Cpu,
      'Backend': Database,
      'Web Platform': Globe,
      'Fintech': Globe,
      'Authentication': Code,
      'AI/Research': Cpu,
      'Data Engineering': Database,
      'AI/Generative': Zap,
      'AI/NLP': Code,
      'AI/Vision': Eye,
      'AI/Audio': Zap
    };
    return icons[category] || Code;
  };

  // Convert original projects to the new format
  const convertedOriginalWebProjects = Object.values(originalWebProjects).map((project, index) => ({
    id: `original-web-${index}`,
    title: project.title,
    subtitle: project.details,
    description: project.details,
    category: 'Web Application',
    type: 'professional',
    companyProject: project.companyProject,
    status: 'completed',
    featured: false,
    technologies: project.technologies,
    images: [],
    demoUrl: null,
    githubUrl: null,
    year: project.year
  }));

  const convertedOriginalAppProjects = Object.values(originalAppProjects).map((project, index) => ({
    id: `original-app-${index}`,
    title: project.title,
    subtitle: project.details,
    description: project.details,
    category: 'Mobile Application',
    type: 'professional',
    companyProject: project.companyProject,
    status: 'completed',
    featured: false,
    technologies: project.technologies,
    images: [],
    demoUrl: null,
    githubUrl: null,
    year: project.year
  }));

  // Combine all projects and sort: personal first, then company projects
  const allProjects = [
    ...convertedOriginalWebProjects.filter(p => !p.companyProject),
    ...convertedOriginalAppProjects.filter(p => !p.companyProject),
    ...featuredProjects.filter(p => !p.companyProject),
    ...otherProjects.filter(p => !p.companyProject),
    ...mlCollaborations.filter(p => !p.companyProject),
    // Company projects at the bottom
    ...convertedOriginalWebProjects.filter(p => p.companyProject),
    ...convertedOriginalAppProjects.filter(p => p.companyProject),
    ...featuredProjects.filter(p => p.companyProject),
    ...otherProjects.filter(p => p.companyProject),
    ...mlCollaborations.filter(p => p.companyProject)
  ];

  const filteredProjects = allProjects.filter(project => {
    if (filter === 'all') return true;
    if (filter === 'featured') return project.featured;

    // AI/ML filter - check category and technologies
    if (filter === 'ai') {
      return project.category?.includes('AI') ||
             project.category?.includes('ML') ||
             project.technologies?.some(tech =>
               ['AI', 'Machine Learning', 'LLM', 'OpenAI', 'Claude', 'Computer Vision',
                'Deep Learning', 'NLP', 'Voice Technology', 'TTS', 'STT'].includes(tech)
             );
    }

    // Web filter - check category and technologies
    if (filter === 'web') {
      return ['Web Platform', 'Web Apps', 'Web Application'].includes(project.category) ||
             project.technologies?.some(tech =>
               ['React', 'Frontend', 'Django', 'Flask', 'Next.js', 'Vue.js'].includes(tech)
             );
    }

    // Mobile filter - check category and technologies
    if (filter === 'mobile') {
      return ['Mobile Application', 'Mobile'].includes(project.category) ||
             project.technologies?.some(tech =>
               ['Flutter', 'Kotlin', 'Android SDK', 'Mobile', 'Cross-platform', 'Android', 'iOS'].includes(tech)
             );
    }

    // Backend filter - check category and technologies
    if (filter === 'backend') {
      return project.category === 'Backend' ||
             project.technologies?.some(tech =>
               ['Node.js', 'Express.js', 'Backend', 'API', 'Python', 'Flask', 'Django',
                'MongoDB', 'MySQL', 'PostgreSQL', 'Database', 'REST', 'GraphQL'].includes(tech)
             );
    }

    return true;
  });

    return (
    <section
      id="portfolio"
      style={{
        padding: '120px 0',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        position: 'relative'
      }}
    >
      {/* Background Animation */}
      <div style={{
        position: 'absolute',
        inset: 0,
        opacity: 0.1
      }}>
        <div style={{
          position: 'absolute',
          top: '30%',
          left: '20%',
          width: '250px',
          height: '250px',
          background: '#60a5fa',
          borderRadius: '50%',
          mixBlendMode: 'multiply',
          filter: 'blur(30px)',
          animation: 'pulse 4s infinite'
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '30%',
          right: '20%',
          width: '300px',
          height: '300px',
          background: '#a855f7',
          borderRadius: '50%',
          mixBlendMode: 'multiply',
          filter: 'blur(30px)',
          animation: 'pulse 4s infinite',
          animationDelay: '2s'
        }}></div>
                </div>
        
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 24px',
        position: 'relative',
        zIndex: 10
      }}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          style={{
            textAlign: 'center',
            marginBottom: '60px'
          }}
        >
          <h2 style={{
            fontSize: '3.5rem',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '24px',
            background: 'linear-gradient(135deg, #60a5fa, #a855f7)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Portfolio
          </h2>
          <p style={{
            fontSize: '1.125rem',
            color: '#d1d5db',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: 1.6
          }}>
            Explore my latest projects, from AI-powered solutions to full-stack applications and machine learning experiments
          </p>
        </motion.div>

        {/* Filter Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '16px',
            marginBottom: '60px'
          }}
        >
          {projectCategories.map((category, index) => (
            <motion.button
              key={category.id}
              onClick={() => handleFilter(category.id)}
              style={{
                padding: '12px 24px',
                background: filter === category.id ? 'rgba(96, 165, 250, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                border: filter === category.id ? '1px solid #60a5fa' : '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: filter === category.id ? '#60a5fa' : 'white',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(12px)'
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
            >
              {category.name} ({category.count})
            </motion.button>
          ))}
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth >= 1024 ? 'repeat(3, 1fr)' :
                                 window.innerWidth >= 768 ? 'repeat(2, 1fr)' : '1fr',
            gap: '32px'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {filteredProjects.map((project, index) => {
            const IconComponent = getCategoryIcon(project.category);
            return (
              <motion.div
                key={project.id}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  cursor: 'pointer'
                }}
                whileHover={{ scale: 1.02, y: -5 }}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.5,
                  delay: 0.5 + index * 0.1,
                  hover: { duration: 0.3 }
                }}
              >
                {/* Project Image */}
                <div style={{
                  height: '200px',
                  background: 'linear-gradient(135deg, #60a5fa, #a855f7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {project.images && project.images.length > 0 ? (
                    <img
                      src={project.images[0]}
                      alt={project.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <div style={{
                      fontSize: '4rem',
                      color: 'white',
                      opacity: 0.8
                    }}>
                      <IconComponent />
                    </div>
                  )}

                  {project.featured && (
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      background: 'rgba(168, 85, 247, 0.9)',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: '500'
                    }}>
                      Featured
                    </div>
                  )}
                </div>

                {/* Project Content */}
                <div style={{
                  padding: '24px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px'
                  }}>
                    <IconComponent size={16} style={{ color: '#60a5fa' }} />
                    <span style={{
                      fontSize: '0.75rem',
                      color: '#d1d5db',
                      background: 'rgba(96, 165, 250, 0.2)',
                      padding: '2px 8px',
                      borderRadius: '12px'
                    }}>
                      {project.category}
                    </span>
                    {project.companyProject && (
                      <span style={{
                        fontSize: '0.7rem',
                        color: '#f59e0b',
                        background: 'rgba(245, 158, 11, 0.1)',
                        padding: '2px 6px',
                        borderRadius: '8px',
                        border: '1px solid rgba(245, 158, 11, 0.3)'
                      }}>
                        Company Project
                      </span>
                    )}
                  </div>

                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 'bold',
                    color: 'white',
                    marginBottom: '8px'
                  }}>
                    {project.title}
                  </h3>

                  <p style={{
                    fontSize: '0.875rem',
                    color: '#60a5fa',
                    marginBottom: '12px'
                  }}>
                    {project.subtitle}
                  </p>

                  <p style={{
                    fontSize: '0.875rem',
                    color: '#d1d5db',
                    lineHeight: 1.5,
                    marginBottom: '16px'
                  }}>
                    {project.description}
                  </p>

                  {/* Technologies */}
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '6px',
                    marginBottom: '20px'
                  }}>
                    {project.technologies?.slice(0, 4).map((tech, i) => (
                      <span key={i} style={{
                        background: 'rgba(168, 85, 247, 0.2)',
                        color: '#a855f7',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}>
                        {tech}
                      </span>
                    ))}
                    {project.technologies?.length > 4 && (
                      <span style={{
                        color: '#d1d5db',
                        fontSize: '0.75rem'
                      }}>
                        +{project.technologies.length - 4} more
                      </span>
                    )}
                  </div>

                                    {/* Project Links */}
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    {project.demoUrl && (
                      <a
                        href={project.demoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          background: 'rgba(96, 165, 250, 0.2)',
                          color: '#60a5fa',
                          borderRadius: '6px',
                          textDecoration: 'none',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'rgba(96, 165, 250, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'rgba(96, 165, 250, 0.2)';
                        }}
                      >
                        <ExternalLink size={12} />
                        Demo
                      </a>
                    )}
                    {project.githubUrl && (
                      <a
                        href={project.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          background: 'rgba(255, 255, 255, 0.1)',
                          color: 'white',
                          borderRadius: '6px',
                          textDecoration: 'none',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        }}
                      >
                        <Github size={12} />
                        Backend
                      </a>
                    )}
                    {project.additionalLinks?.frontend && (
                      <a
                        href={project.additionalLinks.frontend}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          background: 'rgba(16, 185, 129, 0.2)',
                          color: '#10b981',
                          borderRadius: '6px',
                          textDecoration: 'none',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'rgba(16, 185, 129, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'rgba(16, 185, 129, 0.2)';
                        }}
                      >
                        <Github size={12} />
                        Frontend
                      </a>
                    )}
                    {project.additionalLinks?.wordpress && (
                      <a
                        href={project.additionalLinks.wordpress}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          background: 'rgba(139, 92, 246, 0.2)',
                          color: '#8b5cf6',
                          borderRadius: '6px',
                          textDecoration: 'none',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'rgba(139, 92, 246, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'rgba(139, 92, 246, 0.2)';
                        }}
                      >
                        <Github size={12} />
                        WordPress
                      </a>
                    )}
                </div>
                
                  {/* Year Badge */}
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '0.75rem'
                  }}>
                    {project.year}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
            </div>
        </section>
  );
};

export default Portfolio